export const APP_THEME_STORAGE_KEY = "ano-app-theme";
export const APP_THEME_ATTRIBUTE = "data-theme";

export const APP_THEMES = [
  "matcha2",
  "matcha2-light",
  "liquidmatcha",
  "crystal",
] as const;
export type AppTheme = (typeof APP_THEMES)[number];

export const DEFAULT_APP_THEME: AppTheme = "matcha2";

export const APP_THEME_OPTIONS: ReadonlyArray<{
  value: AppTheme;
  label: string;
  description: string;
}> = [
  {
    value: "matcha2",
    label: "Matcha 2",
    description: "Military-olive dark with organic greens",
  },
  {
    value: "matcha2-light",
    label: "Matcha 2 Light",
    description: "Light matcha with organic greens",
  },
  {
    value: "liquidmatcha",
    label: "Liquid Matcha",
    description: "Glass morphism dark theme (macOS 26+)",
  },
  {
    value: "crystal",
    label: "Crystal",
    description: "Colorless liquid glass — all white (macOS 26+)",
  },
];

function getStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function getRootElement(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.documentElement;
}

export function isAppTheme(value: string): value is AppTheme {
  return (APP_THEMES as readonly string[]).includes(value);
}

export function readThemePreference(): AppTheme {
  const storage = getStorage();
  if (!storage) return DEFAULT_APP_THEME;

  try {
    const storedTheme = storage.getItem(APP_THEME_STORAGE_KEY);
    if (storedTheme && isAppTheme(storedTheme)) {
      return storedTheme;
    }
  } catch {
    // Ignore storage read failures and use default theme.
  }

  return DEFAULT_APP_THEME;
}

export function applyThemePreference(theme: AppTheme): void {
  const root = getRootElement();
  if (!root) return;
  root.setAttribute(APP_THEME_ATTRIBUTE, theme);
}

export function initializeThemePreference(): AppTheme {
  const theme = readThemePreference();
  applyThemePreference(theme);
  return theme;
}

export function setThemePreference(theme: AppTheme): void {
  const storage = getStorage();
  if (storage) {
    try {
      storage.setItem(APP_THEME_STORAGE_KEY, theme);
    } catch {
      // Ignore storage write failures and still apply theme in-memory.
    }
  }

  applyThemePreference(theme);
}
